import { useEffect, useState } from 'react';
import Markdown, { type Components, type Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeSlug from 'rehype-slug';
import { toString as hastToString } from 'hast-util-to-string';
import type { Element, Root } from 'hast';
import { visit } from 'unist-util-visit';
import { CodeView, CopyButton } from './code-view';
import { languageLabel } from './highlight';
import { MermaidDiagram } from './mermaid-diagram';
import './course.css';

/*
 * Lesson markdown: GFM (tables, task lists, footnotes), math through KaTeX
 * as MathML (no inline styles, so no KaTeX CSS), class-based highlighting and
 * Mermaid diagrams. Raw HTML from authors is not rendered; the tree is
 * sanitized before our own plugins add math and anchors. KaTeX is loaded only
 * for lessons that contain `$`.
 */

const schema = {
  ...defaultSchema,
  // Heading ids come from rehype-slug after sanitizing, so no clobber prefix is needed for them.
  attributes: {
    ...defaultSchema.attributes,
    code: [['className', /^language-./, 'math-inline', 'math-display']],
  },
};

/**
 * Keeps the output free of inline styles, which the CSP forbids: drops any a
 * plugin adds (KaTeX error spans) and turns table cell alignment into a class
 * (react-markdown would otherwise render `align` as `style="text-align"`).
 */
function rehypeNoInlineStyles() {
  return (tree: Root) => {
    visit(tree, 'element', (el: Element) => {
      const props = el.properties;
      if (!props) return;
      delete props.style;
      if ((el.tagName === 'td' || el.tagName === 'th') && typeof props.align === 'string') {
        const cls = Array.isArray(props.className) ? props.className : [];
        props.className = [...cls, `align-${props.align}`];
        delete props.align;
      }
    });
  };
}

type PluggableList = NonNullable<Options['rehypePlugins']>;
type Pluggable = PluggableList[number];
let katexPlugin: Pluggable | null = null;
let katexPromise: Promise<Pluggable> | null = null;
const loadKatex = () =>
  (katexPromise ??= import('rehype-katex').then((m) => {
    katexPlugin = [m.default, { output: 'mathml', strict: 'ignore' }];
    return katexPlugin;
  }));

const hasMath = (markdown: string) => markdown.includes('$');

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

function isExternal(href: string | undefined): boolean {
  if (!href) return false;
  try {
    const url = new URL(href, window.location.href);
    return url.origin !== window.location.origin && /^https?:$/.test(url.protocol);
  } catch {
    return false;
  }
}

function CodeBlock({ node }: { node?: Element }) {
  const code = node?.children.find(
    (c): c is Element => c.type === 'element' && c.tagName === 'code',
  );
  const classes = (code?.properties.className as string[] | undefined) ?? [];
  const lang = classes.find((c) => c.startsWith('language-'))?.slice('language-'.length) ?? '';
  const text = code ? hastToString(code) : node ? hastToString(node) : '';

  if (lang === 'mermaid') return <MermaidDiagram source={text} />;
  const label = languageLabel(lang);
  return (
    <div className="md-code">
      <CodeView
        code={text}
        language={lang}
        header={
          <div className="flex items-center justify-between gap-2 border-b border-border py-1 pl-3 pr-1">
            <span className="truncate text-xs font-medium text-muted-foreground">
              {label || 'Code'}
            </span>
            <CopyButton text={text.replace(/\n$/, '')} />
          </div>
        }
      />
    </div>
  );
}

const components: Components = {
  pre: ({ node }) => <CodeBlock node={node} />,
  a: ({ node: _node, href, children, ...props }) => {
    const external = isExternal(href);
    return (
      <a
        href={href}
        {...props}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </a>
    );
  },
  img: ({ node: _node, alt, ...props }) => (
    <img {...props} alt={alt ?? ''} loading="lazy" decoding="async" />
  ),
  table: ({ node: _node, ...props }) => (
    <div className="md-table">
      <table {...props} />
    </div>
  ),
};

export function MarkdownContent({ markdown, className }: { markdown: string; className?: string }) {
  const needsMath = hasMath(markdown);
  const [katex, setKatex] = useState<Pluggable | null>(katexPlugin);

  useEffect(() => {
    if (!needsMath || katex) return;
    let cancelled = false;
    loadKatex()
      .then((p) => !cancelled && setKatex(() => p))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [needsMath, katex]);

  // Render once math support is in, so formulas don't flash as source.
  if (needsMath && !katex) {
    return <div className={cx('prose-ready min-h-6', className)} aria-busy />;
  }

  const rehypePlugins: PluggableList = [
    [rehypeSanitize, schema],
    rehypeSlug,
    ...(katex ? [katex] : []),
    rehypeNoInlineStyles,
  ];

  return (
    <div className={cx('prose-ready', className)}>
      <Markdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {markdown}
      </Markdown>
    </div>
  );
}
